import { i18n } from '@marine/i18n';
import {
  celsiusToFahrenheit,
  kilometersPerHourToKnots,
  metersToFeet,
  UNIT_SYSTEMS,
  type UnitSystem,
  withUnit,
} from './unitSystem';

/**
 * The live preview on the onboarding units step: `Preview · 3.2 m depth · 12.4 km/h · 18 °C`,
 * changing with the selection.
 *
 * Written as two hardcoded strings the preview would eventually disagree with what the app
 * actually renders, which is worse than showing nothing — so the numbers come from the same
 * conversions every other surface uses, and only the sample reading and the labels live here.
 *
 * **What Imperial means here**, settled from the code rather than the design copy: distance is
 * nautical miles, speed is knots, depth is feet, temperature is °F. The design's Imperial subtitle
 * reads "miles", which would be statute miles — the app has never meant that. Metric keeps km/h,
 * which is the unit speeds are stored in.
 */

/** One depth, one speed, one temperature — the reading the design's preview line is drawn from. */
export const UNIT_PREVIEW_SAMPLE = {
  depthMeters: 3.2,
  speedKilometersPerHour: 12.4,
  temperatureCelsius: 18,
} as const;

/** The preview shows one decimal; a value that lands on a whole number drops the `.0`. */
const PREVIEW_FRACTION_DIGITS = 1;

const formatNumber = (value: number): string => value.toFixed(PREVIEW_FRACTION_DIGITS).replace(/\.0$/, '');

export type UnitPreview = {
  /** `3.2 m` / `10.5 ft` */
  depth: string;
  /** `12.4 km/h` / `6.7 kn` */
  speed: string;
  /** `18 °C` / `64.4 °F` */
  temperature: string;
};

type UnitSystemLabels = {
  depth: string;
  speed: string;
  temperature: string;
};

const UNIT_LABELS: Record<UnitSystem, UnitSystemLabels> = {
  metric: { depth: 'm', speed: 'km/h', temperature: '°C' },
  imperial: { depth: 'ft', speed: 'kn', temperature: '°F' },
};

/**
 * The option card subtitles, kept beside the preview so the card and the line below it cannot
 * describe different systems.
 *
 * The design reads `miles · knots · feet · °F` for Imperial. `nautical miles` here instead, because
 * mixing statute miles with knots is what raised the question in the first place, and every
 * distance the app prints for an imperial user is already nautical.
 */
export const UNIT_SYSTEM_SUMMARY: Record<UnitSystem, string> = {
  // Getters, so each read is in the UI's language at that moment — this object is shared at module
  // level by both apps' onboarding and preferences screens.
  get metric() {
    return i18n.t('domain:unitSystemSummary.metric');
  },
  get imperial() {
    return i18n.t('domain:unitSystemSummary.imperial');
  },
};

const buildPreview = (system: UnitSystem): UnitPreview => {
  const labels = UNIT_LABELS[system];
  const isImperial = system === UNIT_SYSTEMS.IMPERIAL;

  const depth = isImperial ? metersToFeet(UNIT_PREVIEW_SAMPLE.depthMeters) : UNIT_PREVIEW_SAMPLE.depthMeters;
  const speed = isImperial
    ? kilometersPerHourToKnots(UNIT_PREVIEW_SAMPLE.speedKilometersPerHour)
    : UNIT_PREVIEW_SAMPLE.speedKilometersPerHour;
  const temperature = isImperial
    ? celsiusToFahrenheit(UNIT_PREVIEW_SAMPLE.temperatureCelsius)
    : UNIT_PREVIEW_SAMPLE.temperatureCelsius;

  return {
    depth: withUnit(formatNumber(depth), labels.depth),
    speed: withUnit(formatNumber(speed), labels.speed),
    temperature: withUnit(formatNumber(temperature), labels.temperature),
  };
};

/**
 * Built once per system at module load, so toggling the selection on the units step is a lookup
 * rather than a recalculation — the step re-renders the preview line and nothing else.
 */
const UNIT_PREVIEWS: Record<UnitSystem, UnitPreview> = {
  metric: buildPreview(UNIT_SYSTEMS.METRIC),
  imperial: buildPreview(UNIT_SYSTEMS.IMPERIAL),
};

/** The three preview strings for a unit system. */
export const getUnitPreview = (system: UnitSystem): UnitPreview => UNIT_PREVIEWS[system];
